/*
 * Share a substitute out over the lines of a finding that wraps.
 *
 * "Radek Holomčík" broken after the first name is two finding rectangles; setting all of
 * "Matouš Procházka" on the first line and leaving the second empty reads like a layout bug. The
 * preview gives every line its part of the substitute instead, in reading order and never
 * breaking a word, and each part is then placed and fitted on its own line. A line that crosses
 * table columns is shared out once more, over its columns (`columnSegments`), so a selected table
 * row keeps its columns.
 */

import type { ExportMode, FindingModel, RectModel } from '@/types/documents.types';
import {
  type LineSegment,
  type MeasureText,
  type SubstitutePlacement,
  type TextRun,
  columnSegments,
  placeSubstitute,
  runForRect,
} from '@/utils/substituteLayout';

/** One piece of a substitute — a line's part, or a column's part of a line — and where it goes. */
export interface SubstituteLine {
  text: string;
  placement: SubstitutePlacement;
}

const LINE_BREAK = /\r\n|\r|\n/;

const wordsOf = (text: string): string[] => text.split(/\s+/).filter(Boolean);

/** Length of `words` joined by single spaces. */
const joinedLength = (words: readonly string[]): number =>
  words.reduce((total, word) => total + word.length, 0) + Math.max(words.length - 1, 0);

/**
 * Split `words` into `weights.length` consecutive parts whose lengths follow the weights as
 * closely as whole words allow; a tie goes to the earlier line, and a later line can end up empty.
 */
const splitByWeight = (words: readonly string[], weights: readonly number[]): string[][] => {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  const totalLength = joinedLength(words);
  const parts: string[][] = [];
  let taken = 0;
  let weightSoFar = 0;
  for (let line = 0; line < weights.length; line += 1) {
    if (line === weights.length - 1) {
      parts.push(words.slice(taken));
      break;
    }
    weightSoFar += weights[line];
    const target = totalWeight > 0 ? (weightSoFar / totalWeight) * totalLength : 0;
    let end = taken;
    let bestDistance = Math.abs(joinedLength(words.slice(0, taken)) - target);
    for (let candidate = taken + 1; candidate <= words.length; candidate += 1) {
      const distance = Math.abs(joinedLength(words.slice(0, candidate)) - target);
      if (distance <= bestDistance) {
        end = candidate;
        bestDistance = distance;
      }
    }
    // Text starts where the finding starts: the first line is never the one left empty.
    if (line === 0 && end === 0 && words.length > 0) end = 1;
    parts.push(words.slice(taken, end));
    taken = end;
  }
  return parts;
};

/**
 * The part of `substitute` each of the finding's `lineCount` lines shows; `""` for a line left
 * empty.
 *
 * In order of preference: the substitute's own line breaks, when it kept one per line (dates and
 * amounts are generated character for character); the original's word count per line, when the
 * substitute has as many words ("Mgr. | Radek Holomčík" gives "Mgr. | Matouš Procházka"); and
 * otherwise the share of the text each line held in the original, or, when the original's line
 * breaks do not match the lines, the share of the width each line's rectangle takes.
 */
export const splitAcrossLines = (
  substitute: string,
  original: string,
  lineCount: number,
  lineWidths: readonly number[] = []
): string[] => {
  if (lineCount <= 1) return [substitute.trim()];

  const ownLines = substitute.split(LINE_BREAK);
  if (ownLines.length === lineCount) return ownLines.map((line) => line.trim());

  const words = wordsOf(substitute);
  const originalLines = original.split(LINE_BREAK);
  if (originalLines.length === lineCount) {
    const counts = originalLines.map((line) => wordsOf(line).length);
    if (counts.reduce((sum, count) => sum + count, 0) === words.length) {
      let taken = 0;
      return counts.map((count) => {
        const part = words.slice(taken, taken + count).join(' ');
        taken += count;
        return part;
      });
    }
  }

  const weights =
    originalLines.length === lineCount
      ? originalLines.map((line) => line.trim().length)
      : Array.from({ length: lineCount }, (_, line) => lineWidths[line] ?? 1);
  return splitByWeight(words, weights).map((part) => part.join(' '));
};

/** Text compared the way two text layers agree on it: composed, lower case. */
const comparable = (text: string): string => text.normalize('NFC').toLowerCase();

/**
 * How many of the original line's words each column holds, found by looking them up in order in
 * the text of the column's runs; `null` when a word turns up in none of them (the two text layers
 * disagree on it).
 */
const wordsPerColumn = (originalLine: string, segments: readonly LineSegment[]): number[] | null => {
  const texts = segments.map((segment) => comparable(segment.runs.map((run) => run.text).join(' ')));
  const counts = segments.map(() => 0);
  let column = 0;
  let cursor = 0;
  for (const word of wordsOf(originalLine).map(comparable)) {
    let found = -1;
    while (column < texts.length) {
      found = texts[column].indexOf(word, cursor);
      if (found >= 0) break;
      column += 1;
      cursor = 0;
    }
    if (found < 0) return null;
    counts[column] += 1;
    cursor = found + word.length;
  }
  return counts;
};

/**
 * One line's part of a substitute shared out over the line's columns.
 *
 * With as many words as the original line, each column gets as many as it had (a scrambled table
 * row keeps its word breaks, so "Zálohování (Veeam, VM snapshoty) x" gives its last word to the
 * third column); otherwise the words follow the columns' widths.
 */
const splitAcrossColumns = (part: string, originalLine: string, segments: readonly LineSegment[]): string[] => {
  if (segments.length <= 1) return [part];
  const words = wordsOf(part);
  const counts = wordsPerColumn(originalLine, segments);
  if (counts && counts.reduce((sum, count) => sum + count, 0) === words.length) {
    let taken = 0;
    return counts.map((count) => {
      const piece = words.slice(taken, taken + count).join(' ');
      taken += count;
      return piece;
    });
  }
  const widths = segments.map((segment) => Math.max(segment.rect.x1 - segment.rect.x0, 0));
  return splitByWeight(words, widths).map((piece) => piece.join(' '));
};

/**
 * Split a substitute over its finding's lines, and every line over its columns, and place each
 * piece where it goes: the baseline, font, size and horizontal scale of the run it replaces,
 * fitted to its own room. Per finding rectangle the pieces on that line, left to right; empty for
 * a line that got no part.
 *
 * `lineRunsOf` gives the runs of a rectangle's line (`runsForRect`); without any the rectangle is
 * one piece placed from its own height.
 */
export const placeSubstituteLines = (
  substitute: string,
  original: string,
  rects: readonly RectModel[],
  lineRunsOf: (rect: RectModel) => TextRun[],
  measure: MeasureText
): SubstituteLine[][] => {
  const widths = rects.map((rect) => Math.max(rect.x1 - rect.x0, 0));
  const parts = splitAcrossLines(substitute, original, rects.length, widths);
  const originalLines = original.split(LINE_BREAK);
  return rects.map((rect, index) => {
    const part = parts[index];
    if (!part) return [];
    const segments = columnSegments(rect, lineRunsOf(rect));
    // The original line's own words are known only when its line breaks match the rectangles.
    const originalLine = originalLines.length === rects.length ? originalLines[index] : '';
    const pieces = splitAcrossColumns(part, originalLine, segments);
    return segments.flatMap((segment, column) => {
      const text = pieces[column];
      if (!text) return [];
      const run = runForRect(segment.rect, segment.runs);
      return [{ text, placement: placeSubstitute(text, segment.rect, run, measure) }];
    });
  });
};

/**
 * `pageText[start, end)` as the export mode shows it, for the clipboard; mirrors the backend's
 * `GET /documents/{id}/text`. A finding the range only touches is replaced whole. Redacted turns
 * every non-whitespace character of a finding into `*`; anonymized gives each of its lines its
 * share of the substitute, and a finding without one (a declaration) becomes empty.
 */
export const replaceFindingsInRange = (
  pageText: string,
  start: number,
  end: number,
  findings: readonly Pick<FindingModel, 'start' | 'end' | 'substitute'>[],
  mode: Exclude<ExportMode, 'original'>
): string => {
  const touched = findings
    .filter((finding) => finding.start < end && start < finding.end)
    .sort((a, b) => a.start - b.start);
  let position = Math.min(start, touched[0]?.start ?? start);
  const parts: string[] = [];
  for (const finding of touched) {
    if (finding.start < position) continue;
    parts.push(pageText.slice(position, finding.start));
    const original = pageText.slice(finding.start, finding.end);
    if (mode === 'redacted') {
      parts.push(original.replace(/\S/g, '*'));
    } else {
      const breaks = original.match(new RegExp(LINE_BREAK, 'g')) ?? [];
      const lines = splitAcrossLines(finding.substitute ?? '', original, breaks.length + 1);
      parts.push(lines.map((line, index) => line + (breaks[index] ?? '')).join(''));
    }
    position = finding.end;
  }
  parts.push(pageText.slice(position, Math.max(end, position)));
  return parts.join('').replace(/\r\n?/g, '\n');
};
